/// <reference path="../types.d.ts" />

import history from '../history';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../core/net';
import {
  DictionaryValues,
  DictionaryUpload,
  ColumnMetadataEditor,
  ArchiveModal
} from './action-types';

import {
  showFeedback,
  dismissFeedback,
  showFeedbackError,
  showFeedbackWarning
} from './ui';
import { filterStatusFields } from '../reducers/state/dictionary-values';
import { ActionToast } from '../views/ui/action-toast';
import { commonEnums, HttpMethods, cmxErrors } from '@codametrix/ui-common';
import { isNewDesign } from '../core/middleware/new-design-middleware';
import { cmxDateTime, AmplifyCore } from '@codametrix/ui-common';
import {
  savePotentialArchives,
  selectArchiveRow,
  toggleArchiveModal
} from './dictionary-archive-row.actions';

const { paginationUtils } = AmplifyCore;

const { BadRequestError } = cmxErrors;
const { DictionaryStatus, FilterTypes } = commonEnums;

const chooseRow = createAction<CMxAPI.DictionaryRow>(
  DictionaryValues.CHOOSE_ROW
);

const chooseColumnReference = createAction<CMxAPI.ColumnDefinition>(
  DictionaryUpload.CHOOSE_COL_REFERENCE
);

const removeColumnMetadata = createAction<CMxAPI.ColumnDefinitionMetadata>(
  DictionaryUpload.REMOVE_COLUMN_METADATA
);

const setStatusFilter = createAction<string>(
  DictionaryValues.SET_STATUS_FILTER
);

const selectKeyColumn = createAction<number>(
  ColumnMetadataEditor.SELECT_KEY_COLUMN
);

const confirmMappingsExist = createAction<boolean>(
  ColumnMetadataEditor.CONFIRM_MAPPINGS_EXISTS
);

const setDictionaryMappings = createAction<CMxAPI.ColumnDefinitionMetadata[]>(
  ColumnMetadataEditor.SET_DICTIONARY_MAPPINGS
);

const selectActiveColumn = createAction<CMxAPI.ColumnDefinition>(
  ColumnMetadataEditor.SELECT_ACTIVE_COLUMN
);

const removeColumnMapping = createAction<CMxAPI.ColumnDefinition>(
  ColumnMetadataEditor.REMOVE_COLUMN_MAPPING
);

const saveColumnMapping = createAction<CMx.saveColumnMappingPayload>(
  ColumnMetadataEditor.SAVE_COLUMN_MAPPING
);

const setArchiveChildren = createAction<boolean>(
  ArchiveModal.ARCHIVE_CHILDREN
);

const toggleDeleteModal = createAction<boolean>(
  DictionaryValues.TOGGLE_DELETE_MODAL
);

const togglePublishModal = createAction<boolean>(
  DictionaryValues.TOGGLE_PUBLISH_MODAL
);

const toggleUploadType = createAction<boolean>(
  DictionaryUpload.TOGGLE_UPLOAD_TYPE
);

const toggleInfoModal = createAction<boolean>(
  DictionaryValues.TOGGLE_INFO_MODAL
);

const toggleSecurityModal = createAction<boolean>(
  DictionaryValues.TOGGLE_SECURITY_MODAL
);

const updateDictionaryValue = createAction<AppProps.DefaultStateData>(
  DictionaryValues.UPDATE_COLUMN_DEFS
);

//We want to use async so  we can get a loadstate change to cause a rerender on the form component
const initUpload = createAsyncThunk<
  void,
  CMxAPI.Dictionary,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryUpload.UPLOAD_INIT, async () => {
  return;
});

/** async to reload form, thus allowing the forms state to refresh */
const addColumnMetadata = createAsyncThunk<
  CMxAPI.ColumnDefinitionMetadata,
  CMxAPI.ColumnDefinitionMetadata,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryUpload.ADD_COLUMN_METADATA, async (value, { dispatch }) => {
  return value;
});

const loadValue = createAsyncThunk<
  number,
  AppProps.actionParams<number>,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.LOAD_VALUE, async (params, { dispatch }) => {
  const { path, data: rowId } = params;

  history.push(path + `/row/${rowId}/edit`);
  return rowId;
});

export const shimDraftFilter = (filters: CMxCommonApp.Filter[]) => {
  const shimmedFilters = filters.filter(
    filter => !['edit_status', 'status'].includes(filter.key)
  );

  shimmedFilters.push(
    { key: 'status', terms: ['draft', 'published'], type: 'IN' },
    {
      key: 'edit_status',
      terms: ['ADD', 'UPDATE', null],
      type: 'IN'
    } as any
  );
  return shimmedFilters;
};

const getDraftFilters = async (
  filters: CMxCommonApp.Filter[],
  dictionaryId: number
) => {
  const isDraft = filters.filter((f: any) => {
    const isEqual = f.terms.includes('draft');
    return isEqual;
  }).length;
  if (!isDraft) return null;
  const deleteAndUpdateRowSearch = api<
    CMxAPI.PageableList<CMxAPI.DictionaryRow>
  >({
    endpoint: `/dictionary/${dictionaryId}/row/v2?page=0&size=1&listType=column`,
    init: {
      method: HttpMethods.POST
    },
    body: [
      {
        key: 'status',
        terms: ['published'],
        type: 'IN'
      },
      {
        key: 'edit_status',
        terms: ['DELETE', 'UPDATE'],
        type: 'IN'
      }
    ]
  });

  const draftRowSearch = api<CMxAPI.PageableList<CMxAPI.DictionaryRow>>({
    endpoint: `/dictionary/${dictionaryId}/row/v2?page=0&size=1&listType=column`,
    init: {
      method: HttpMethods.POST
    },
    body: [
      {
        key: 'status',
        terms: ['draft'],
        type: 'IN'
      }
    ]
  });
  const [deletedAndUpdateRows, draftRows] = await Promise.all([
    deleteAndUpdateRowSearch,
    draftRowSearch
  ]);
  if (
    deletedAndUpdateRows?.content.length !== 0 ||
    draftRows?.content.length !== 0
  ) {
    const shimmedDraftFilters = shimDraftFilter(filters);
    return shimmedDraftFilters;
  }
  return null;
};

export const getDictionaryFilters = async (
  dictionaryId: number,
  sortablePageable: CMxCommonApp.SortablePageable<any>
) => {
  const {
    filterableOptions: { filters = [] }
  } = sortablePageable;

  const statusTerm = filters.find(f => f.key === 'status')?.terms[0];

  // Filtering for Deleted Rows, Added Rows, Or updated rows requires shimming in an editStatus filter.
  const shimEditStatusFilter = filterStatusFields.find(
    field => field.key === statusTerm
  )?.transform;

  let shimmedFilters: CMxCommonApp.Filter[] = filters;
  if (shimEditStatusFilter) {
    shimmedFilters = shimEditStatusFilter(filters);
  }

  let shimmedDraftFilters: CMxCommonApp.Filter[] | null = shimmedFilters;

  const draftFilters = await getDraftFilters(filters, dictionaryId);
  if (draftFilters) shimmedDraftFilters = draftFilters;
  return shimmedDraftFilters;
};

const checkIfKeyColumnPresent = (
  dictionary: CMxAPI.Dictionary,
  dispatch: any
) => {
  const hasKeyColumn: boolean =
    dictionary.columnDefinitions.find(col => {
      return col.keyColumnIndicator === true;
    }) !== undefined;

  if (!hasKeyColumn) {
    dispatch(
      showFeedbackWarning({
        message: ActionToast({
          actionText: 'here',
          message: `Key column not set. Please select one`,
          onAction: (path: string) => {
            history.push(`${path}/metadata/keyColumn`);
            dispatch(dismissFeedback(true));
          }
        }),
        meta: { level: 'WARNING' },
        dismissable: true,
        className: 'feedback-error'
      }) as any
    );
  }
};
/**
 * Listing draft rows may not include all the rows of a draft dictionary.
 * Some may be a part of the existing published dictionary.
 * Therefore we need to check to see if any rows exist in the published dictionary with editStatus ADD/UPDATE/DELETE
 **/
const listItems = createAsyncThunk<
  CMxAPI.PageableList<CMxAPI.DictionaryRow>,
  {
    dictionary: CMxAPI.Dictionary;
    sortablePageable: CMxCommonApp.SortablePageable<any>;
  },
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.LIST_VALUES, async (params, { dispatch }) => {
  const { dictionary, sortablePageable } = params;

  var searchParams = paginationUtils.searchParamsFactory(sortablePageable);

  const filters = await getDictionaryFilters(dictionary.id, sortablePageable);
  let pageableList = await api<CMxAPI.PageableList<CMxAPI.DictionaryRow>>({
    endpoint: `/dictionary/${
      dictionary.id
    }/row/v2?${searchParams.toString()}&listType=column`,
    init: {
      method: HttpMethods.POST
    },
    body: filters || []
  });

  return pageableList;
});

const loadDictionary = createAsyncThunk<
  CMxAPI.Dictionary,
  AppProps.listParams,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.LOAD_DICTIONARY, async (params, { dispatch }) => {
  dispatch(
    showFeedback({
      id: Date.now(),
      message: `Loading dictionary values...`,
      dismissable: false,
      className: 'feedback-working'
    }) as any
  );

  const { id, sortablePageable } = params;

  const dictionary = await api<CMxAPI.Dictionary>({
    endpoint: `/dictionary/${id}/filters/v2?pageSize=0`,
    init: {
      method: HttpMethods.POST
    },
    body: []
  });
  dispatch(initializeColumnMetadataEditor(dictionary) as any);
  !isNewDesign && dispatch(listItems({ dictionary, sortablePageable }) as any);
  checkIfKeyColumnPresent(dictionary, dispatch);
  dispatch(dismissFeedback(true) as any);

  return dictionary;
});

const saveDictionaryRow = createAsyncThunk<
  CMxAPI.DictionaryRowDto,
  CMxAPI.DictionaryRowDto,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.SAVE_VALUE, async (value, { dispatch }) => {
  const endpoint = `/dictionary/row/v2`;

  const res = await api<CMxAPI.DictionaryRowDto>({
    endpoint: endpoint,
    init: {
      method: value.id ? HttpMethods.PUT : HttpMethods.POST
    },
    body: value
  });

  return res;
});

const listColumnValues = createAsyncThunk<
  CMxAPI.CellDto[],
  CMxAPI.ColumnDefinition,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.LIST_COLUMN_VALUES, async (value, { dispatch }) => {
  const { cellReferences, tenantId } = value;
  const { dictionaryName, lookupProperty: columnName } = cellReferences[0];

  // getting the dictionary id from dictionary name and tenant id
  const dictionaryEndpoint = `/dictionary/${dictionaryName}/list/v2?tenantId=${tenantId.uuid}`;

  const dictionary = await api<CMxAPI.Dictionary[]>({
    endpoint: dictionaryEndpoint,
    init: {
      method: HttpMethods.GET
    }
  });

  const endpoint = `/dictionary/${dictionary[0].id}/cell/v2?page=0&size=1000&listType=column`;

  const res = await api<CMxAPI.PageableList<CMxAPI.CellDto>>({
    endpoint: endpoint,
    init: {
      method: HttpMethods.POST
    },
    body: [
      {
        key: columnName,
        terms: [''],
        type: 'LIKE'
      }
    ]
  });

  return res.content;
});

const listDictionaryOptions = createAsyncThunk<
  CMxAPI.DictionaryOptionsObj,
  { tenantId: any; orgName: string },
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryUpload.LIST_DICTIONARIES, async (params, { dispatch }) => {
  const pageableList = await api<CMxAPI.PageableList<CMxAPI.Dictionary>>({
    endpoint: `/dictionary/v2?tenantId=${
      params.tenantId
    }&sort=name%2CASC&size=1000&&derivedIndicator=false&uniqueByName=true&status=${DictionaryStatus.PUBLISHED.toUpperCase()}`,
    init: {
      method: HttpMethods.GET
    }
  });

  return { pageableList: pageableList, organizationName: params.orgName };
});

const listColumnOptions = createAsyncThunk<
  CMxAPI.ColumnDefinition[],
  {
    tenantId: string;
    dictionaryName: string;
  },
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryUpload.LIST_COLUMNS, async (params, { dispatch }) => {
  const dateStr = cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);
  const pageableList = await api<CMxAPI.ColumnDefinition[]>({
    endpoint: `/dictionary/${params.dictionaryName}/column-definition/v2?derivedIndicator=false&effectiveDateStr=${dateStr}&tenantId=${params.tenantId}`,
    init: {
      method: HttpMethods.GET
    }
  });

  return pageableList;
});

//export for testing
export const getPublishableDictionaryIds = (
  associatedDictionaries: CMxAPI.Dictionary[]
): [number | undefined, number | undefined] => {
  const publishedDictionaryId = associatedDictionaries.find(dict => {
    return dict.status === DictionaryStatus.PUBLISHED.toUpperCase();
  })?.id;

  let draftDictionaryId = associatedDictionaries.find(dict => {
    return dict.status === DictionaryStatus.DRAFT.toUpperCase();
  })?.id;

  if (publishedDictionaryId && draftDictionaryId) {
    return [draftDictionaryId, publishedDictionaryId];
  }

  if (publishedDictionaryId && !draftDictionaryId) {
    return [publishedDictionaryId, undefined];
  }
  return [draftDictionaryId, undefined];
};

const publishDictionary = createAsyncThunk<
  CMxAPI.DictionaryRequestResponse,
  { assocDictionaries: CMxAPI.Dictionary[]; jiraTicket: string },
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.PUBLISH_DICTIONARY, async (params, { dispatch }) => {
  const { assocDictionaries, jiraTicket } = params;
  const dateStr = cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);

  const [recentDictionaryId, optOldDictionaryId] = getPublishableDictionaryIds(
    assocDictionaries
  );

  let searchParams = new URLSearchParams();
  searchParams.set('effectiveDateStr', dateStr);
  searchParams.set('jiraTicket', jiraTicket);

  if (optOldDictionaryId) {
    searchParams.set('oldDictionaryId', optOldDictionaryId.toString());
  }

  const uploadResponse = await api<CMxAPI.DictionaryRequestResponse>({
    endpoint: `/dictionary/${recentDictionaryId}/publish/v2?${searchParams.toString()}`,
    init: {
      method: HttpMethods.PUT
    }
  }).catch(e => {
    if (e instanceof BadRequestError) {
      dispatch(dismissFeedback(false) as any);
      dispatch(
        showFeedbackError({
          id: Date.now(),
          message: `Dictionary Publish Unsuccessful`,
          dismissable: true
        }) as any
      );
    }
    throw e;
  });

  const successRoute = window.location.pathname
    .split('/')
    .slice(0, 6)
    .join('/');

  const navParams = new URLSearchParams({
    requestId: uploadResponse.id.toString()
  });

  history.push(successRoute + `/request?${navParams}`);

  return uploadResponse;
});

const downloadDictionaryByRequestID = createAsyncThunk<
  void,
  { requestId: string },
  { rejectValue: CMxCommonApp.SubmitError }
>(
  DictionaryValues.DOWNLOAD_DICTIONARY_BY_REQUEST_ID,
  async (payload, { dispatch }) => {
    const { requestId } = payload;

    dispatch(
      showFeedback({
        id: Date.now(),
        message: `Downloading Dictionary. Please Wait`,
        dismissable: true,
        className: 'feedback-working'
      }) as any
    );

    const endpoint = `/dictionary/request/${requestId}/csv/v2`;

    const response = await api<any>(
      {
        endpoint,
        headers: {
          'Content-Type': 'application/csv'
        }
      },
      false
    ).catch(e => {
      dispatch(dismissFeedback(false) as any);
      dispatch(
        showFeedbackError({
          id: Date.now(),
          message: `Dictionary Download Unsuccessful`,
          dismissable: true
        }) as any
      );
    });

    const filename = response.contentDisposition?.split('=')[1];

    var link = document.createElement('a');
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(response.blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    dispatch(
      showFeedback({
        id: Date.now(),
        message: `Dictionary Download Finished`,
        dismissable: true,
        className: 'feedback-working'
      }) as any
    );
  }
);

const downloadDictionary = createAsyncThunk<
  void,
  CMx.dictionaryDownloadPayload,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.DOWNLOAD_DICTIONARY, async (payload, { dispatch }) => {
  const {
    dictionaryName,
    tenantId,
    effectiveDate,
    updatedDate,
    withDraft
  } = payload;

  let searchParams = new URLSearchParams();
  searchParams.append('tenantId', tenantId);
  if (withDraft) {
    searchParams.append('withDraft', 'true');
  }
  if (effectiveDate) {
    searchParams.append(
      'effectiveDateStr',
      updatedDate
        ? updatedDate
        : cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE)
    );
  }

  dispatch(
    showFeedback({
      id: Date.now(),
      message: `Downloading Dictionary. Please Wait`,
      dismissable: true,
      className: 'feedback-working'
    }) as any
  );

  const endpoint = `/dictionary/${encodeURI(
    dictionaryName
  )}/csv/v2?${searchParams.toString()}`;

  const response = await api<any>(
    {
      endpoint,
      headers: {
        'Content-Type': 'application/csv'
      }
    },
    false
  ).catch(e => {
    dispatch(dismissFeedback(false) as any);
    dispatch(
      showFeedbackError({
        id: Date.now(),
        message: `Dictionary Download Unsuccessful`,
        dismissable: true
      }) as any
    );
  });

  const filename = response.contentDisposition?.split('=')[1];

  var link = document.createElement('a');
  if (link.download !== undefined) {
    // feature detection
    // Browsers that support HTML5 download attribute
    var url = URL.createObjectURL(response.blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  dispatch(
    showFeedback({
      id: Date.now(),
      message: `Dictionary Download Finished`,
      dismissable: true,
      className: 'feedback-working'
    }) as any
  );
});

const listAssociatedDictionaries = createAsyncThunk<
  CMxAPI.Dictionary[],
  CMxAPI.Dictionary,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  DictionaryValues.LIST_ASSOCIATED_DICTIONARIES,
  async (dictionary, { dispatch }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('tenantId', dictionary.tenantId.uuid);
    const dictionaries =
      (await api<CMxAPI.Dictionary[]>({
        endpoint: `/dictionary/${
          dictionary.name
        }/list/v2?${searchParams.toString()}`
      })) || [];

    return dictionaries;
  }
);

/**
 * Async function to get history of a dictionary
 * @param DictionaryValues.LIST_DICTIONARY_HISTORY
 * @param <Function>
 * @retuns
 */
const listDictionaryHistory = createAsyncThunk<
  CMxAPI.DictionaryHistory[],
  Number,
  { rejectValue: CMxCommonApp.CMxError }
>(DictionaryValues.LIST_DICTIONARY_HISTORY, async (dictionaryId, { dispatch }) => {
  // default row params
  const defaultRowParams = new URLSearchParams({
    page: '0',
    size: '200'
  });

  // create necessary params
  const rowBody = [
    {
      key: 'dictionary_id',
      terms: [dictionaryId],
      type: FilterTypes.EQ
    }
  ];

  // request to API
  const history = await api<{ content: CMxAPI.DictionaryHistory[] }>({
    endpoint: `/dictionary/request/v2?${defaultRowParams.toString()}`,
    init: {
      method: HttpMethods.POST
    },
    body: rowBody
  }).catch(e => {
    if (e instanceof BadRequestError) {
      dispatch(dismissFeedback(false) as any);
      dispatch(
        showFeedbackError({
          id: Date.now(),
          message: `Getting History Dictionary Unsuccessful`,
          dismissable: true
        }) as any
      );
    }
    throw e;
  });

  return history.content;
});

const initializeColumnMetadataEditor = createAsyncThunk<
  CMxAPI.ColumnDefinition[],
  CMxAPI.Dictionary,
  { rejectValue: CMxCommonApp.CMxError }
>(ColumnMetadataEditor.INITIALIZE, async (dictionary, { dispatch }) => {
  const searchParams = new URLSearchParams();
  searchParams.append('tenantId', dictionary.tenantId.uuid);
  searchParams.append('derivedIndicator', 'false');
  if (dictionary.effectiveDate && dictionary.status !== 'PUBLISHED') {
    searchParams.append('effectiveDateStr', dictionary.effectiveDate);
  } else if (dictionary.status !== 'DRAFT') {
    searchParams.append(
      'effectiveDateStr',
      dictionary.updatedDate ??
        cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE)
    );
  }
  const cols = await api<CMxAPI.ColumnDefinition[]>({
    endpoint: `/dictionary/${
      dictionary.name
    }/column-definition/v2?${searchParams.toString()}`
  });

  const hasKeyColumn = cols?.some(col => col.keyColumnIndicator === true);

  if (!hasKeyColumn && cols?.length > 0) {
    cols[0].keyColumnIndicator = true;
  }
  return cols;
});

const saveColumnDefinitions = createAsyncThunk<
  CMxAPI.Dictionary,
  CMx.dictionaryMetadataSavePayload,
  { rejectValue: CMxCommonApp.CMxError }
>(ColumnMetadataEditor.SAVE_COLUMN_METADATA, async (payload, { dispatch }) => {
  //Column definition save call

  const { columnDefinitions, succesPath, dictionaryId } = payload;

  const res = await api<CMxAPI.DictionaryRequestError>({
    endpoint: `/dictionary/${dictionaryId}/column-definition/v2`,
    init: {
      method: HttpMethods.PUT
    },
    body: columnDefinitions
  });

  if (res && res.status === 400) {
    dispatch(
      showFeedbackError({
        id: Date.now(),
        message: `Metadata Save Unsuccessful!`,
        dismissable: true
      }) as any
    );
    throw res;
  }

  const updatedDictionary = await api<CMxAPI.Dictionary>({
    endpoint: `/dictionary/${dictionaryId}/filters/v2?pageSize=0`,
    init: {
      method: HttpMethods.POST
    },
    body: []
  });

  history.push(succesPath);
  dispatch(
    showFeedback({
      id: Date.now(),
      message: `Metadata saved succesfully`,
      dismissable: true,
      className: 'feedback-working'
    }) as any
  );

  return updatedDictionary;
});

const archiveRow = createAsyncThunk<
  CMxAPI.DictionaryRow[],
  CMx.archiveRowPayload,
  { rejectValue: CMxCommonApp.CMxError }
>(
  ArchiveModal.ARCHIVE_ROW,
  async (payload, { dispatch }): Promise<CMxAPI.DictionaryRow[]> => {
    const { row, archiveChildren, dictionary, sortablePageable, jiraTicket } =
      payload;

    const keyValue = row.cells.find(
      cell => cell.columnDefinition.keyColumnIndicator
    )?.value;

    let archivedRows: CMxAPI.DictionaryRow[] = [];
    if (archiveChildren && keyValue && jiraTicket) {
      const searchParams = new URLSearchParams({
        endDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
        rowKey: keyValue,
        tenantId: row.tenantId.uuid,
        jiraTicket
      });

      archivedRows = await api<CMxAPI.DictionaryRow[]>({
        endpoint: `/dictionary/${
          row.dictionaryName
        }/row/archive/v2?${searchParams.toString()}`,
        init: {
          method: HttpMethods.PUT
        }
      });
    }
    const searchParams = new URLSearchParams({
      endDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
      jiraTicket
    });

    const archivedRow = await api<CMxAPI.DictionaryRow>({
      endpoint: `/dictionary/row/${
        row.id
      }/archive/v2?${searchParams.toString()}`,
      init: {
        method: HttpMethods.PUT
      }
    });

    dispatch(listItems({ dictionary, sortablePageable }) as any);
    return [archivedRow, ...archivedRows];
  }
);

const deleteDictionary = createAsyncThunk<
  boolean,
  string[],
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.DELETE_DICTIONARY, async (deleteIds, { dispatch }) => {
  const deleteResponses = await Promise.all(
    deleteIds.map(id => {
      return (
        api<CMxAPI.DictionaryRequestResponse>({
          endpoint: `/dictionary/${id}/v2`,
          init: {
            method: HttpMethods.DELETE
          }
        }) || false
      );
    })
  ).catch(e => {
    if (e instanceof BadRequestError) {
      dispatch(dismissFeedback(false) as any);
      dispatch(
        showFeedbackError({
          id: Date.now(),
          message: `Dictionary Delete Unsuccessful`,
          dismissable: true
        }) as any
      );
    }
    throw e;
  });

  const successRoute = window.location.pathname
    .split('/')
    .slice(0, 6)
    .join('/');

  let navParams = new URLSearchParams();
  deleteResponses.forEach(res => {
    navParams.append('requestId', res.id.toString());
  });

  history.replace(successRoute + `/request?${navParams.toString()}`);

  return false;
});

const updateDictionaryDescription = createAsyncThunk<
  CMxAPI.Dictionary,
  CMxAPI.Dictionary,
  { rejectValue: CMxCommonApp.CMxError }
>(DictionaryValues.UPDATE_DICTIONARY_DESCRIPTION, async (payload, { dispatch }) => {
  const updatedDictionary = await api<CMxAPI.Dictionary>({
    endpoint: `/dictionary/description/v2`,
    init: {
      method: HttpMethods.PUT
    },
    body: payload
  });

  return updatedDictionary;
});

const unrestrictDictionary = createAsyncThunk<
  void,
  Number,
  { rejectValue: CMxCommonApp.CMxError }
>(
  DictionaryValues.UNRESTRICT_DICTIONARY,
  async (dictionaryId, { dispatch }) => {
    await api<CMxAPI.Dictionary>({
      endpoint: `/dictionary/${dictionaryId}/unrestricted/v2`,
      init: {
        method: HttpMethods.PUT
      }
    }).catch(e => {
      dispatch(dismissFeedback(false) as any);
      dispatch(
        showFeedbackError({
          id: Date.now(),
          message: `Dictionary Unrestrict Unsuccessful`,
          dismissable: true
        }) as any
      );
    });
  }
);

const applyFilters = createAsyncThunk<
  string,
  string,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  DictionaryValues.SET_STATUS_FILTER,
  async (status, { dispatch }) => {
    dispatch(setStatusFilter(status));
    return status;
  }
);

export const syncActions = {
  chooseRow,
  setStatusFilter,
  selectKeyColumn,
  confirmMappingsExist,
  setDictionaryMappings,
  selectActiveColumn,
  saveColumnMapping,
  removeColumnMapping,
  toggleArchiveModal,
  setArchiveChildren,
  savePotentialArchives,
  updateDictionaryValue
};

export {
  listItems,
  loadDictionary,
  loadValue,
  saveDictionaryRow,
  listDictionaryOptions,
  listColumnOptions,
  publishDictionary,
  addColumnMetadata,
  removeColumnMetadata,
  chooseColumnReference,
  initUpload,
  downloadDictionary,
  downloadDictionaryByRequestID,
  listAssociatedDictionaries,
  listDictionaryHistory,
  initializeColumnMetadataEditor,
  saveColumnDefinitions,
  selectArchiveRow,
  archiveRow,
  toggleDeleteModal,
  deleteDictionary,
  toggleUploadType,
  toggleInfoModal,
  toggleSecurityModal,
  togglePublishModal,
  unrestrictDictionary,
  updateDictionaryDescription,
  applyFilters,
  getDraftFilters,
  listColumnValues
};
